/**
 * <GenerateActivityOverlay> — « Générer une activité », monté seulement quand il est ouvert.
 *
 * `useGenerateActivityFlow` installe une trentaine de crochets, quatre tranches d'appel et leurs
 * effets. Posé dans l'écran, il vivait avec l'onglet — et les onglets du parent restent montés
 * (`unmountOnBlur: false`), donc le parcours travaillait en permanence pour une surface que
 * personne n'avait ouverte. Le parcours est donc enfermé ici : l'écran garde un booléen
 * d'ouverture, ce composant n'existe que pendant, et l'effet de démontage du crochet rend les
 * quatre tranches au repos en partant.
 */
import React, { useCallback, useEffect, useRef } from "react"

import { GenerateActivityFlow } from "../../../../../../design-system"
import { useGenerateActivityFlow } from "../../../hooks/useGenerateActivityFlow"

export type GenerateActivityOverlayProps = {
    /** L'enfant — son identifiant part dans l'URL de création. */
    studentId?: string | number | null
    /** Rejoué après une création : la liste doit montrer la nouvelle activité. */
    onCreated?: () => void
    /** Faux ⇒ le parcours n'est pas monté du tout. */
    open: boolean
    /** L'écran repose son booléen — c'est lui qui démonte le parcours. */
    onClose: () => void
}

export function GenerateActivityOverlay(props: GenerateActivityOverlayProps) {
    const { open, ...rest } = props
    if (!open) return null
    return <MountedGenerateActivityOverlay {...rest} />
}

function MountedGenerateActivityOverlay(props: Omit<GenerateActivityOverlayProps, "open">) {
    const { studentId, onCreated, onClose } = props
    const flow = useGenerateActivityFlow({ studentId, onCreated })

    // Le montage EST l'ouverture. La garde tient parce que `open` se redéclare dès que le compte
    // de l'élève change d'identité : sans elle, un rechargement du compte remettrait le brouillon
    // en cours à zéro.
    const openFlow = flow.open
    const openedRef = useRef(false)
    useEffect(() => {
        if (openedRef.current) return
        openedRef.current = true
        openFlow()
    }, [openFlow])

    const close = flow.generate.onClose
    const handleClose = useCallback(() => {
        close()
        onClose()
    }, [close, onClose])

    return <GenerateActivityFlow {...flow.generate} onClose={handleClose} />
}
